import { InputAdornment } from '@mui/material';
import { DialogType } from '../../../@types';
import {
  BettingSlipAcceptStatus,
  BettingSlipFrom,
  BettingSlipStatus,
  BettingSlipType,
  Customer,
  Device,
  Partner,
} from '../../../@types/api';
import {
  BETTING_SLIP_ACCEPT_STATUS_OPTIONS,
  BETTING_SLIP_AUTHORIZED_TICKETS_OPTIONS,
  BETTING_SLIP_BET_BUILDER_OPTIONS,
  BETTING_SLIP_FROM_OPTIONS,
  BETTING_SLIP_IS_WAYS_OPTIONS,
  BETTING_SLIP_REOFFERED_OPTIONS,
  BETTING_SLIP_STATUS_OPTIONS,
  BETTING_SLIP_TYPE_OPTIONS,
  DEVICE_OPTIONS,
  QUERY_KEYS,
} from '../../../constants';
import { BETTING_SLIP_ACCEPT_STATUS } from '../../../constants/enums';
import { USER_ROLES } from '../../../constants/keycloak';
import useFilterForm from '../../../hooks/useFilterForm';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import { getCompleteName, prepareBoolValue } from '../../../utils';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormNumberInput from '../../molecules/FormNumberInput';
import FormSelect from '../../molecules/FormSelect';
import RiskFactorSelect from '../../molecules/RiskFactorSelect';
import FormAutocomplete from '../FormAutocomplete';

export const defaultMonitoringFilters: FilterTicketMonitoringData = {
  from: undefined,
  bettingSlipType: undefined,
  acceptStatus: BETTING_SLIP_ACCEPT_STATUS.ACCEPTED,
  resolveStatuses: [],
  device: undefined,
  isWays: undefined,
  betBuilder: undefined,
  cashOut: undefined,
  riskFactorId: undefined,
  reoffered: undefined,
  authorizedTickets: undefined,
  userId: undefined,
  operatorId: undefined,
  agentId: undefined,
  bonusPercentage: undefined,
};

export type FilterTicketMonitoringData = {
  from?: BettingSlipFrom;
  bettingSlipType?: BettingSlipType;
  acceptStatus?: BettingSlipAcceptStatus;
  resolveStatuses?: BettingSlipStatus[];
  device?: Device;
  isWays?: boolean;
  betBuilder?: boolean;
  cashOut?: string;
  riskFactorId?: string;
  reoffered?: boolean;
  authorizedTickets?: string;
  userId?: string;
  operatorId?: string;
  agentId?: string;
  bonusPercentage?: string;
};

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterTicketMonitoring = ({ changeQuery, closeModal }: Props) => {
  const prepareData = (data: FilterTicketMonitoringData) => {
    const preparedData = { ...data };

    preparedData.isWays = prepareBoolValue(preparedData.isWays);
    preparedData.betBuilder = prepareBoolValue(preparedData.betBuilder);
    preparedData.reoffered = prepareBoolValue(preparedData.reoffered);

    return preparedData;
  };

  const { control, handleResetFilter, handleSubmit, watch } = useFilterForm<FilterTicketMonitoringData>({
    defaultData: defaultMonitoringFilters,
    prepareQueryParams: prepareData,
    changeQuery,
  });

  const agentId = watch('agentId');

  return (
    <FilterDialogLayout
      label='tickets'
      onSave={handleSubmit}
      onClose={() => closeModal?.()}
      onReset={handleResetFilter}
    >
      <FormAutocomplete<Partner, FilterTicketMonitoringData>
        name='agentId'
        control={control}
        queryKey={[QUERY_KEYS.agentSearch, 'agent']}
        apiService='crm'
        url='agent/search'
        queryParams={{
          roleTypes: [USER_ROLES.AGENT],
        }}
        label='Agent'
        getOptionLabel={(options, value) => {
          const option = options.find((option) => option?.id === value);
          return option ? option.fullName : '';
        }}
      />
      <FormAutocomplete<Customer, FilterTicketMonitoringData>
        name='userId'
        control={control}
        queryKey={[QUERY_KEYS.customers, agentId]}
        apiService='crm'
        url='customer/search'
        queryParams={{
          filters: {
            accountManager: {
              values: [agentId],
            },
          },
        }}
        label='Player'
        hookEnabled
        getOptionLabel={(options, value) => {
          const option = options.find((option) => option?.userId === value);
          return option ? `${getCompleteName(option.firstName, option.lastName, option.username)}` : '';
        }}
        optionsFilter='userId'
      />
      <FormSelect control={control} name='from' label='From' options={BETTING_SLIP_FROM_OPTIONS} />
      <FormSelect control={control} name='bettingSlipType' label='Ticket type' options={BETTING_SLIP_TYPE_OPTIONS} />
      <FormSelect control={control} name='acceptStatus' label='Status' options={BETTING_SLIP_ACCEPT_STATUS_OPTIONS} />
      <FormSelect
        control={control}
        name='resolveStatuses'
        label='Resolution status'
        options={BETTING_SLIP_STATUS_OPTIONS}
        multiple
        closeOnSelect
      />
      <FormSelect control={control} name='reoffered' label='Reoffered' options={BETTING_SLIP_REOFFERED_OPTIONS} />
      <FormSelect
        control={control}
        name='authorizedTickets'
        label='Authorized tickets'
        options={BETTING_SLIP_AUTHORIZED_TICKETS_OPTIONS}
      />

      <FormAutocomplete<Partner, FilterTicketMonitoringData>
        name='operatorId'
        control={control}
        queryKey={[QUERY_KEYS.agentSearch, 'operator']}
        apiService='crm'
        url='agent/search'
        queryParams={{
          roleTypes: [USER_ROLES.ADMIN, USER_ROLES.TRADER],
        }}
        label='Betting operator'
        getOptionLabel={(options, value) => {
          const option = options.find((option) => option?.id === value);
          return option ? option.fullName : '';
        }}
      />
      <FormSelect control={control} name='device' label='Ip/Source' options={DEVICE_OPTIONS} />
      <FormSelect control={control} name='isWays' label='Ways' options={BETTING_SLIP_IS_WAYS_OPTIONS} />
      <FormSelect control={control} name='betBuilder' label='Bet Builder' options={BETTING_SLIP_BET_BUILDER_OPTIONS} />
      <RiskFactorSelect control={control} name='riskFactorId' />
      <FormNumberInput
        control={control}
        name='bonusPercentage'
        label='Bonus percentage'
        allowDecimals
        valueAsString
        slotProps={{
          input: {
            endAdornment: <InputAdornment position='end'>%</InputAdornment>,
          },
        }}
      />
    </FilterDialogLayout>
  );
};

export default FilterTicketMonitoring;
